import { useMemo, useState } from "react";
import { Plus } from "@repo/ui";
import { useTasks } from "~/apis/use-tasks";
import BigButton from "~/components/ui/big-button";
import { PageContentLayout } from "~/components/ui/page-content-layout";
import { PageHeader } from "~/components/ui/page-header";
import { PageLayout } from "~/components/ui/page-layout";
import { useConfigurationSync, useFileSync } from "~/hooks/use-sync";
import { getActiveLabel } from "~/pages/dashboard/task-manager/brand-row/use-task-columns";
import { BrandRow } from "./brand-row/brand-row";
import { useTaskDialog } from "./task-dialog/use-task-dialog";
import { TaskManagerToolbar } from "./task-manager-toolbar";
import { useBrands } from "./use-brands";
import { useWorkflows } from "./use-workflows";

export const TaskManagerPage = () => {
  useConfigurationSync("Brand");
  useConfigurationSync("Customer");
  useConfigurationSync("Workflow");
  useConfigurationSync("Folder");
  useFileSync("Task");

  const { showTaskDialog } = useTaskDialog();
  const { brands } = useBrands();
  const { workflows } = useWorkflows();

  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedBrands, setExpandedBrands] = useState<Set<number>>(new Set());
  const [shouldFetchTasks, setShouldFetchTasks] = useState(false);

  const { tasks: allTasks, isLoading: isLoadingTasks } = useTasks(shouldFetchTasks);

  const toggleBrand = (brandId: number) => {
    setExpandedBrands((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(brandId)) {
        newSet.delete(brandId);
      } else {
        newSet.add(brandId);
      }
      return newSet;
    });
  };

  const handleBrandHover = () => {
    if (!shouldFetchTasks) {
      setShouldFetchTasks(true);
    }
  };

  const handleSearchFocus = () => {
    if (!shouldFetchTasks) {
      setShouldFetchTasks(true);
    }
  };

  const sortedBrands = useMemo(
    () =>
      brands
        ? [...brands].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
          )
        : [],
    [brands]
  );

  const filteredBrands = useMemo(() => {
    if (!sortedBrands) return [];

    const lowerSearchQuery = searchQuery.trim().toLowerCase();

    if (!lowerSearchQuery) {
      return sortedBrands;
    }

    if (isLoadingTasks || workflows.length === 0) {
      return sortedBrands;
    }

    if (allTasks.length === 0) {
      return [];
    }

    const workflowById = new Map(workflows.map((workflow) => [workflow.id, workflow]));
    const tasksByBrandId = new Map<number, typeof allTasks>();

    for (const task of allTasks) {
      if (!showAll && !task.active) continue;

      const brandId = task.brandId;
      if (brandId === undefined) continue;

      const brandTasks = tasksByBrandId.get(brandId);

      if (brandTasks) {
        brandTasks.push(task);
      } else {
        tasksByBrandId.set(brandId, [task]);
      }
    }

    return sortedBrands.filter((brand) => {
      const brandNameMatches = brand.name.toLowerCase().includes(lowerSearchQuery);

      const brandTasks = tasksByBrandId.get(brand.id);

      if (!brandTasks) return brandNameMatches;

      const hasMatchingTasks = brandTasks.some((task) => {
        const workflow = workflowById.get(task.workflowId);
        return (
          task.name?.toLowerCase().includes(lowerSearchQuery) ||
          getActiveLabel(task.active)?.toLowerCase().includes(lowerSearchQuery) ||
          workflow?.name?.toLowerCase().includes(lowerSearchQuery) ||
          task.taskFields?.some((tf) =>
            tf.value !== null && tf.value !== undefined
              ? String(tf.value).toLowerCase().includes(lowerSearchQuery)
              : false
          )
        );
      });

      return brandNameMatches || hasMatchingTasks;
    });
  }, [sortedBrands, searchQuery, allTasks, workflows, isLoadingTasks, showAll]);

  return (
    <PageLayout>
      <PageHeader
        title="Task Manager"
        controls={
          <BigButton onClick={() => showTaskDialog()} icon={<Plus />} iconPosition="right">
            <span className="max-lg:hidden">Create</span>
          </BigButton>
        }
      />
      <PageContentLayout>
        <TaskManagerToolbar
          showAll={showAll}
          onShowAllChange={setShowAll}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onSearchFocus={handleSearchFocus}
        />

        <div className="space-y-4">
          {filteredBrands.map((brand) => {
            const isExpanded = expandedBrands.has(brand.id);

            return (
              <BrandRow
                key={brand.id}
                brand={brand}
                isExpanded={isExpanded}
                onToggle={() => toggleBrand(brand.id)}
                onHover={handleBrandHover}
                searchQuery={searchQuery}
                showAll={showAll}
                showTaskDialog={showTaskDialog}
              />
            );
          })}

          {filteredBrands.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center">
              <p className="text-gray-500">
                {searchQuery ? "No tasks match your search" : "No brands found"}
              </p>
            </div>
          )}
        </div>
      </PageContentLayout>
    </PageLayout>
  );
};
